import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { parseGpxText } from "../lib/gpx";

import { isYouTube, loadYouTubeAPI, parseYouTubeId } from "../lib/youtube";
import MapView from "../components/MapView";
import StandaloneRouteForm from "../components/RouteForm";
import StandaloneVideoForm from "../components/VideoForm";
import { Button } from "../components/ui/button";
import Pagination from "../components/Pagination";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import PlaylistForm from "../components/PlaylistForm";
import VideoMarkersEditor from "../components/VideoMarkersEditor";

import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  PlusIcon,
  PencilIcon,
  SpinnerGapIcon,
  TrashIcon,
  WarningIcon,
  WrenchIcon,
  DropIcon,
  FlagBannerIcon,
  ForkKnifeIcon,
  ShoppingCartIcon,
  MapPinIcon,
  BinocularsIcon,
  TentIcon,
  BedIcon,
} from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
function renderMarkdownInline(source = "") {
  const text = String(source || "");
  // very small inline markdown: **bold**, *em*, `code`
  const parts = [];
  let rest = text;
  const tokenRe = /(\*\*[^\*]+\*\*|\*[^\*]+\*|`[^`]+`)/;
  while (rest.length) {
    const m = rest.match(tokenRe);
    if (!m) {
      parts.push(rest);
      break;
    }
    const idx = m.index;
    if (idx > 0) parts.push(rest.slice(0, idx));
    const token = m[0];
    if (token.startsWith("**")) {
      parts.push(<strong key={parts.length}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("*")) {
      parts.push(<em key={parts.length}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith("`")) {
      parts.push(<code key={parts.length}>{token.slice(1, -1)}</code>);
    } else {
      parts.push(token);
    }
    rest = rest.slice(idx + token.length);
  }
  return parts;
}
const CREATED_READY_STATUS_OPTIONS = ["created", "ready"];

const POI_TYPE_META = {
  hazard: { label: "Hazard", iconName: "Warning", Icon: WarningIcon },
  water_source: { label: "Water Source", iconName: "Drop", Icon: DropIcon },
  restaurant: {
    label: "Restaurant",
    iconName: "ForkKnife",
    Icon: ForkKnifeIcon,
  },
  grocery_store: {
    label: "Grocery Store",
    iconName: "ShoppingCart",
    Icon: ShoppingCartIcon,
  },
  bike_repair: { label: "Bike Repair", iconName: "Wrench", Icon: WrenchIcon },
  transportation: {
    label: "Transportation",
    iconName: "MapPin",
    Icon: MapPinIcon,
  },
  scenic_viewpoint: {
    label: "Scenic Viewpoint",
    iconName: "Binoculars",
    Icon: BinocularsIcon,
  },
  historical_marker: {
    label: "Historical Marker",
    iconName: "FlagBanner",
    Icon: FlagBannerIcon,
  },
  camping: {
    label: "Camping",
    iconName: "Tent",
    Icon: TentIcon,
  },
  lodging: {
    label: "Lodging",
    iconName: "Bed",
    Icon: BedIcon,
  },
  other: { label: "Other", iconName: "MapPin", Icon: MapPinIcon },
};

const POI_TYPES = Object.keys(POI_TYPE_META);
const getPoiTypeMeta = (type) => POI_TYPE_META[type] || POI_TYPE_META.other;
const POI_TYPE_HELP = POI_TYPES.join(" | ");

function MarkdownPreview({ markdown = "" }) {
  const lines = String(markdown || "").split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let listItems = [];
  let inCodeBlock = false;
  let codeLines = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push(
      <p
        key={`p-${blocks.length}`}
        className="text-sm leading-6 text-[#2C2F2A]"
      >
        {renderMarkdownInline(paragraph.join(" "))}
      </p>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul
        key={`ul-${blocks.length}`}
        className="list-disc space-y-1 pl-5 text-sm leading-6 text-[#2C2F2A]"
      >
        {listItems.map((item, index) => (
          <li key={`li-${index}`}>{renderMarkdownInline(item)}</li>
        ))}
      </ul>,
    );
    listItems = [];
  };

  const flushCodeBlock = () => {
    if (!codeLines.length) return;
    blocks.push(
      <pre
        key={`code-${blocks.length}`}
        className="overflow-x-auto rounded-lg bg-[#1F2421] px-3 py-2 text-xs text-[#F7F5F0]"
      >
        <code>{codeLines.join("\n")}</code>
      </pre>,
    );
    codeLines = [];
  };

  for (const line of lines) {
    if (line.trim().startsWith("```")) {
      flushParagraph();
      flushList();
      if (inCodeBlock) {
        flushCodeBlock();
      }
      inCodeBlock = !inCodeBlock;
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = headingMatch[1].length;
      const className =
        level === 1
          ? "text-xl font-black text-[#1A1D1A]"
          : level === 2
            ? "text-lg font-bold text-[#1A1D1A]"
            : "text-base font-semibold text-[#1A1D1A]";
      blocks.push(
        <div key={`h-${blocks.length}`} className={className}>
          {renderMarkdownInline(headingMatch[2])}
        </div>,
      );
      continue;
    }

    const quoteMatch = trimmed.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      flushList();
      blocks.push(
        <blockquote
          key={`q-${blocks.length}`}
          className="border-l-4 border-[#C86B4E] pl-3 italic text-sm text-[#5C665F]"
        >
          {renderMarkdownInline(quoteMatch[1])}
        </blockquote>,
      );
      continue;
    }

    const listMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (listMatch) {
      flushParagraph();
      listItems.push(listMatch[1]);
      continue;
    }

    flushList();
    paragraph.push(trimmed);
  }

  flushParagraph();
  flushList();
  flushCodeBlock();

  if (!blocks.length) {
    return (
      <div className="text-sm text-[#8A867C]">Nothing to preview yet.</div>
    );
  }

  return <div className="space-y-3">{blocks}</div>;
}

const clamp = (val, min, max) => Math.min(max, Math.max(min, val));
const roundToTenth = (val) => Math.round((Number(val) || 0) * 10) / 10;

function getRoutePointsByID(routes = [], routeID = "") {
  const route = (routes || []).find((r) => r.id === routeID);
  return route?.gpx_points || route?.gpxPoints || [];
}

function normalizeGpxPointsInput(raw) {
  const toPoints = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .filter((p) => Array.isArray(p) && p.length >= 2)
      .map((p) => p.map((v) => Number(v) || 0));
  };

  if (Array.isArray(raw)) {
    return toPoints(raw);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return toPoints(parsed);
      if (parsed && typeof parsed === "object") {
        return toPoints(
          parsed.gpx_points || parsed.gpxPoints || parsed.points || [],
        );
      }
    } catch {
      return [];
    }
    return [];
  }

  if (raw && typeof raw === "object") {
    return toPoints(raw.gpx_points || raw.gpxPoints || raw.points || []);
  }

  return [];
}

function getPointAtTime(gpxPoints = [], seconds = 0, duration = 0) {
  if (!Array.isArray(gpxPoints) || gpxPoints.length === 0) return null;
  if (gpxPoints.length === 1) {
    return [Number(gpxPoints[0][0]) || 0, Number(gpxPoints[0][1]) || 0];
  }

  const total = Number(duration) || 0;
  const t = Number(seconds) || 0;
  const progress = total > 0 ? clamp(t / total, 0, 1) : 0;
  const idx = Math.round(progress * (gpxPoints.length - 1));
  const p = gpxPoints[idx] || gpxPoints[0];
  return [Number(p[0]) || 0, Number(p[1]) || 0];
}

function buildDefaultMarker(videoDraft = {}, routes = []) {
  const routePoints = getRoutePointsByID(routes, videoDraft?.route_id);
  const first = routePoints[0];
  const last = routePoints[routePoints.length - 1] || first;
  const startLat = Number(first?.[0]) || 0;
  const startLng = Number(first?.[1]) || 0;
  const endLat = Number(last?.[0]) || startLat;
  const endLng = Number(last?.[1]) || startLng;
  const duration = Math.max(0, Number(videoDraft?.duration) || 0);

  return {
    from_sec: 0,
    to_sec: roundToTenth(duration),
    start_lat: startLat,
    start_lng: startLng,
    end_lat: endLat,
    end_lng: endLng,
    radius_meters: null,
  };
}

function ensureMarkers(videoDraft = {}, routes = []) {
  const existing = Array.isArray(videoDraft?.markers) ? videoDraft.markers : [];
  if (existing.length > 0) return existing;
  return [buildDefaultMarker(videoDraft, routes)];
}

function TourMarkerEditor({
  markers = [],
  onChange,
  routeID = "",
  routes = [],
}) {
  const [selectedMarkerID, setSelectedMarkerID] = useState(
    markers[0]?.id || null,
  );
  const routePoints = useMemo(
    () => getRoutePointsByID(routes, routeID),
    [routes, routeID],
  );

  useEffect(() => {
    if (!markers.length) {
      setSelectedMarkerID(null);
      return;
    }
    if (!markers.some((marker) => marker.id === selectedMarkerID)) {
      setSelectedMarkerID(markers[0].id);
    }
  }, [markers, selectedMarkerID]);

  const selectedIndex = markers.findIndex(
    (marker) => marker.id === selectedMarkerID,
  );

  const markerPois = useMemo(
    () =>
      (markers || []).map((marker) => {
        const [lat, lng] = getTourMarkerLocation(marker);
        return {
          id: marker.id,
          title: marker.title || "Untitled marker",
          description: "",
          lat,
          lng,
          icon: "MapPin",
          type: "other",
        };
      }),
    ``[markers],
  );

  const updateMarkers = (nextMarkers, nextSelectedID = selectedMarkerID) => {
    const normalized = hydrateTourMarkerLinks(nextMarkers);
    onChange(normalized);
    setSelectedMarkerID(nextSelectedID || normalized[0]?.id || null);
  };

  const setMarker = (index, patch) => {
    const next = [...markers];
    next[index] = {
      ...next[index],
      ...patch,
      location: {
        ...next[index]?.location,
        ...patch.location,
      },
    };
    updateMarkers(next, next[index]?.id);
  };

  const addMarker = () => {
    const newMarker = createDefaultTourMarker(routePoints, markers.length);
    updateMarkers([...(markers || []), newMarker], newMarker.id);
  };

  const moveMarker = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= markers.length) return;
    const next = [...markers];
    [next[index], next[target]] = [next[target], next[index]];
    updateMarkers(next, next[target]?.id);
  };

  const removeMarker = (index) => {
    const next = [...markers];
    const removed = next.splice(index, 1)[0];
    const fallbackSelectedID =
      next[Math.max(0, index - 1)]?.id || next[0]?.id || null;
    updateMarkers(
      next,
      selectedMarkerID === removed?.id ? fallbackSelectedID : selectedMarkerID,
    );
  };

  const onMapClick = ({ lat, lng }) => {
    if (selectedIndex < 0 || !markers[selectedIndex]) return;
    setMarker(selectedIndex, {
      location: {
        lat: Number(lat.toFixed(6)),
        lng: Number(lng.toFixed(6)),
      },
    });
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="flex items-center justify-between">
        <Label>Tour markers</Label>
        <Button
          type="button"
          size="sm"
          onClick={addMarker}
          data-testid="tour-marker-add-button"
        >
          <PlusIcon size={14} className="mr-1" /> Add marker
        </Button>
      </div>
      <div className="rounded-xl overflow-hidden border border-[#E5E0D8] h-72 bg-white">
        {routePoints.length > 1 || markerPois.length > 0 ? (
          <MapView
            gpxPoints={routePoints}
            pois={markerPois}
            activePoiId={selectedMarkerID}
            onMapClick={onMapClick}
            height="100%"
            fit
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-sm text-[#5C665F] px-4 text-center">
            Choose a route, then add markers and click the map to place them.
          </div>
        )}
      </div>
      <div className="text-xs text-[#5C665F]">
        Marker order determines each marker&apos;s previous and next links
        automatically.
      </div>
      <div
        className="space-y-3 max-h-80 overflow-y-auto pr-1"
        data-testid="tour-marker-list"
      >
        {(markers || []).map((marker, index) => {
          const [lat, lng] = getTourMarkerLocation(marker);
          const selected = marker.id === selectedMarkerID;
          return (
            <div
              key={marker.id || `tour-marker-${index}`}
              className={`rounded-xl border p-3 ${selected ? "border-[#2C5545] bg-[#2C5545]/5" : "border-[#E5E0D8] bg-white"}`}
              onClick={() => setSelectedMarkerID(marker.id)}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold">Marker {index + 1}</div>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => moveMarker(index, -1)}
                    disabled={index === 0}
                    data-testid={`tour-marker-move-up-${index}`}
                  >
                    Up
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => moveMarker(index, 1)}
                    disabled={index === markers.length - 1}
                    data-testid={`tour-marker-move-down-${index}`}
                  >
                    Down
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removeMarker(index)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="sm:col-span-3">
                  <Label>Title</Label>
                  <Input
                    data-testid={`tour-marker-title-${index}`}
                    value={marker.title || ""}
                    onChange={(e) =>
                      setMarker(index, { title: e.target.value })
                    }
                  />
                </div>
                <div className="sm:col-span-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div>
                    <Label>Body (Markdown)</Label>
                    <Textarea
                      data-testid={`tour-marker-body-${index}`}
                      rows={8}
                      placeholder="# Camp briefing\n\n- Arrival checklist\n- Gear notes\n\n[Route guide](https://example.com)"
                      value={marker.body || ""}
                      onChange={(e) =>
                        setMarker(index, { body: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Preview</Label>
                    <div
                      data-testid={`tour-marker-preview-${index}`}
                      className="min-h-[180px] rounded-lg border border-[#E5E0D8] bg-[#FCFAF6] p-3"
                    >
                      <MarkdownPreview markdown={marker.body || ""} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Latitude</Label>
                  <Input
                    data-testid={`tour-marker-lat-${index}`}
                    type="text"
                    inputMode="decimal"
                    step="0.000001"
                    value={marker?.location?.lat ?? ""}
                    onChange={(e) =>
                      setMarker(index, { location: { lat: e.target.value } })
                    }
                  />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input
                    data-testid={`tour-marker-lng-${index}`}
                    type="text"
                    inputMode="decimal"
                    step="0.000001"
                    value={marker?.location?.lng ?? ""}
                    onChange={(e) =>
                      setMarker(index, { location: { lng: e.target.value } })
                    }
                  />
                </div>
                <div className="text-xs text-[#5C665F] flex flex-col justify-end">
                  <div>Prev: {marker.previous_marker_id || "-"}</div>
                  <div>Next: {marker.next_marker_id || "-"}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [routesPage, setRoutesPage] = useState(1);
  const [routesPageSize] = useState(20);
  const [routesTotal, setRoutesTotal] = useState(0);
  const [routesSearch, setRoutesSearch] = useState("");
  const [routesStatusFilter, setRoutesStatusFilter] = useState("");
  const [routesSort] = useState("name");
  const routesSearchDebounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (routesSearchDebounceRef.current) {
        clearTimeout(routesSearchDebounceRef.current);
      }
    };
  }, []);
  const [videos, setVideos] = useState([]);
  const [reprocessingVideoIDs, setReprocessingVideoIDs] = useState(
    () => new Set(),
  );
  const [playlists, setPlaylists] = useState([]);
  const [playlistsPage, setPlaylistsPage] = useState(1);
  const [playlistsPageSize] = useState(20);
  const [playlistsTotal, setPlaylistsTotal] = useState(0);
  const [creators, setCreators] = useState([]);
  const [creatorsPage, setCreatorsPage] = useState(1);
  const [creatorsPageSize] = useState(20);
  const [creatorsTotal, setCreatorsTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize] = useState(20);
  const [usersTotal, setUsersTotal] = useState(0);
  const [tours, setTours] = useState([]);
  const [toursPage, setToursPage] = useState(1);
  const [toursPageSize] = useState(20);
  const [toursTotal, setToursTotal] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesPageSize] = useState(20);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [editTour, setEditTour] = useState(null);
  const [selectedTourByUser, setSelectedTourByUser] = useState({});
  const [entitlingUserIDs, setEntitlingUserIDs] = useState(() => new Set());
  const [editCreator, setEditCreator] = useState(null);
  const [editPlaylist, setEditPlaylist] = useState(null);
  const [playlistRouteContext, setPlaylistRouteContext] = useState(null);
  const [videoPlaylistContext, setVideoPlaylistContext] = useState(null);
  const [tourRouteContext, setTourRouteContext] = useState(null);

  const [contextPlaylists, setContextPlaylists] = useState([]);
  const [contextPlaylistsPage, setContextPlaylistsPage] = useState(1);
  const [contextPlaylistsPageSize] = useState(20);
  const [contextPlaylistsTotal, setContextPlaylistsTotal] = useState(0);

  const [contextTours, setContextTours] = useState([]);
  const [contextToursPage, setContextToursPage] = useState(1);
  const [contextToursPageSize] = useState(20);
  const [contextToursTotal, setContextToursTotal] = useState(0);

  const [editRoute, setEditRoute] = useState(null);
  const [editVideo, setEditVideo] = useState(null);
  const [markerVideo, setMarkerVideo] = useState(null);
  const [markerDraft, setMarkerDraft] = useState([]);
  const [markerCurrentTime, setMarkerCurrentTime] = useState(0);
  const [markerDuration, setMarkerDuration] = useState(0);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(null);
  const [isMarkerSelectionLocked, setIsMarkerSelectionLocked] = useState(true);
  const [staticMarkerLengthSec, setStaticMarkerLengthSec] = useState(10);
  const [staticMarkerRadiusMeters, setStaticMarkerRadiusMeters] = useState(100);
  const [mapPickMode, setMapPickMode] = useState(null);

  const contextVideos = useMemo(() => {
    if (!videoPlaylistContext?.id) return [];
    return videos
      .filter(
        (video) =>
          (video.playlist_id || video.group_id || "") ===
          videoPlaylistContext.id,
      )
      .slice()
      .sort(
        (a, b) =>
          (Number(a.index) || 0) - (Number(b.index) || 0) ||
          new Date(a.created_at || 0) - new Date(b.created_at || 0),
      );
  }, [videos, videoPlaylistContext]);

  const loadAll = async () => {
    try {
      await Promise.all([
        loadRoutes(routesPage),
        loadPlaylists(playlistsPage),
        loadCreators(creatorsPage),
        loadUsers(usersPage),
        loadTours(toursPage),
        loadPurchases(purchasesPage),
      ]);
    } catch (e) {
      toast.error("Failed to load admin content");
    }
  };

  const loadRoutes = async (page = 1) => {
    const params = { page, page_size: routesPageSize, sort: routesSort };
    if (routesSearch && String(routesSearch).trim())
      params.search = String(routesSearch).trim();
    if (routesStatusFilter) params.status = routesStatusFilter;
    const { data: rs } = await api.get("/admin/routes", {
      params,
      withCredentials: true,
    });
    const adminRoutes = Array.isArray(rs) ? rs : rs.items || [];
    setRoutes(adminRoutes);
    setRoutesTotal(rs.total || adminRoutes.length || 0);
    const details = await Promise.all(
      adminRoutes.map((r) => api.get(`/admin/routes/${r.id}`)),
    );
    const vs = details.flatMap((resp) => resp.data.videos || []);
    setVideos(vs);
  };

  const loadPlaylists = async (page = 1) => {
    const { data: ps } = await api.get(`/admin/playlists`, {
      params: { page, page_size: playlistsPageSize },
    });
    const items = Array.isArray(ps) ? ps : ps.items || [];
    setPlaylists(items);
    setPlaylistsTotal(ps.total || items.length || 0);
  };

  const upsertRouteInState = (routeDetail) => {
    if (!routeDetail?.id) return;
    setRoutes((prev) => {
      let matched = false;
      const next = prev.map((route) => {
        if (route.id !== routeDetail.id) return route;
        matched = true;
        return {
          ...route,
          ...routeDetail,
          gpx_points:
            routeDetail.gpx_points ||
            routeDetail.gpxPoints ||
            route.gpx_points ||
            route.gpxPoints ||
            [],
          pois: routeDetail.pois || route.pois || [],
        };
      });
      if (matched) return next;
      return [...prev, routeDetail];
    });
  };

  const ensureRouteDetails = async (routeID) => {
   // console.log("ensureRouteDetails", routeID);
    if (!routeID) return null;
    try {
      const { data } = await api.get(`/routes/${routeID}`);
      const routeDetail = {
        ...(data.route || {}),
      };
      //console.log("fetched route details", routeDetail);
      upsertRouteInState(routeDetail);
      return routeDetail;
    } catch {
      return null;
    }
  };

  const ensurePlaylistDetails = async (playlistID) => {
    if (!playlistID) return null;
    try {
      const { data } = await api.get(`/admin/playlists/${playlistID}`);
      if (data?.id) {
        setPlaylists((prev) => {
          let matched = false;
          const next = prev.map((playlist) => {
            if (playlist.id !== data.id) return playlist;
            matched = true;
            return { ...playlist, ...data };
          });
          if (matched) return next;
          return [...prev, data];
        });
      }
      return data || null;
    } catch {
      return null;
    }
  };

  const ensureVideoDetails = async (videoID) => {
    if (!videoID) return null;
    try {
      const { data } = await api.get(`/admin/videos/${videoID}`);
      if (data?.id) {
        setVideos((prev) => {
          let matched = false;
          const next = prev.map((video) => {
            if (video.id !== data.id) return video;
            matched = true;
            return { ...video, ...data };
          });
          if (matched) return next;
          return [...prev, data];
        });
      }
      return data || null;
    } catch {
      return null;
    }
  };

  const loadCreators = async (page = 1) => {
    const { data: cs } = await api.get(`/admin/creators`, {
      params: { page, page_size: creatorsPageSize },
    });
    const items = Array.isArray(cs) ? cs : cs.items || [];
    setCreators(items);
    setCreatorsTotal(cs.total || items.length || 0);
  };

  const loadUsers = async (page = 1) => {
    const { data: us } = await api.get(`/admin/users`, {
      params: { page, page_size: usersPageSize },
    });
    const items = Array.isArray(us) ? us : us.items || [];
    setUsers(items);
    setUsersTotal(us.total || items.length || 0);
  };

  const loadTours = async (page = 1) => {
    const { data: ts } = await api.get(`/admin/tours`, {
      params: { page, page_size: toursPageSize },
    });
    const items = Array.isArray(ts) ? ts : ts.items || [];
    setTours(items);
    setToursTotal(ts.total || items.length || 0);
  };

  const loadPurchases = async (page = 1) => {
    const { data: ps } = await api.get(`/admin/purchases`, {
      params: { page, page_size: purchasesPageSize },
    });
    const items = Array.isArray(ps) ? ps : ps.items || [];
    setPurchases(items);
    setPurchasesTotal(ps.total || items.length || 0);
  };

  const loadContextPlaylists = async (page = 1) => {
    if (!playlistRouteContext?.id) {
      setContextPlaylists([]);
      setContextPlaylistsTotal(0);
      return;
    }
    const { data } = await api.get(`/admin/playlists`, {
      params: {
        page,
        page_size: contextPlaylistsPageSize,
        route_id: playlistRouteContext.id,
      },
    });
    const items = Array.isArray(data) ? data : data.items || [];
    setContextPlaylists(items);
    setContextPlaylistsTotal(data.total || items.length || 0);
  };

  const loadContextTours = async (page = 1) => {
    if (!tourRouteContext?.id) {
      setContextTours([]);
      setContextToursTotal(0);
      return;
    }
    const { data } = await api.get(`/admin/tours`, {
      params: {
        page,
        page_size: contextToursPageSize,
        route_id: tourRouteContext.id,
      },
    });
    const items = Array.isArray(data) ? data : data.items || [];
    setContextTours(items);
    setContextToursTotal(data.total || items.length || 0);
  };

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && !user.is_admin) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.is_admin) loadRoutes(routesPage).catch(() => {});
  }, [user, loading, routesPage]);

  useEffect(() => {
    if (playlistRouteContext?.id) {
      setContextPlaylistsPage(1);
    } else {
      setContextPlaylists([]);
    }
  }, [playlistRouteContext]);

  useEffect(() => {
    if (playlistRouteContext?.id)
      loadContextPlaylists(contextPlaylistsPage).catch(() => {});
  }, [playlistRouteContext, contextPlaylistsPage]);

  useEffect(() => {
    if (tourRouteContext?.id) {
      setContextToursPage(1);
    } else {
      setContextTours([]);
    }
  }, [tourRouteContext]);

  useEffect(() => {
    if (tourRouteContext?.id)
      loadContextTours(contextToursPage).catch(() => {});
  }, [tourRouteContext, contextToursPage]);

  useEffect(() => {
    if (user?.is_admin) loadPlaylists(playlistsPage).catch(() => {});
  }, [user, loading, playlistsPage]);

  useEffect(() => {
    if (user?.is_admin) loadCreators(creatorsPage).catch(() => {});
  }, [user, loading, creatorsPage]);

  useEffect(() => {
    if (user?.is_admin) loadUsers(usersPage).catch(() => {});
  }, [user, loading, usersPage]);

  useEffect(() => {
    if (user?.is_admin) loadTours(toursPage).catch(() => {});
  }, [user, loading, toursPage]);

  useEffect(() => {
    if (user?.is_admin) loadPurchases(purchasesPage).catch(() => {});
  }, [user, loading, purchasesPage]);

  const saveRoute = async () => {
    try {
      if (editRoute.id) {
        await api.put(`/admin/routes/${editRoute.id}`, editRoute);
        toast.success("Route updated");
      } else {
        await api.post("/admin/routes", editRoute);
        toast.success("Route created");
      }
      setEditRoute(null);
      loadAll();
    } catch {
      toast.error("Failed to save route");
    }
  };

  const parseAdminGpxFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/admin/parse-gpx", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const fetchAdminRouteMetadata = async (url) => {
    const { data } = await api.post("/admin/bikepacking/metadata", { url });
    return data;
  };

  const fetchVideoMetadata = async (url) => {
    const { data } = await api.post("/me/videos/metadata", { url });
    return data;
  };

  const saveVideo = async () => {
    try {
      // Normalize sentinel playlist value before sending
      const payload = { ...editVideo };
      const rawDuration = Number(
        payload.duration ?? payload.length ?? payload.video_length,
      );
      payload.duration = Number.isFinite(rawDuration)
        ? Math.max(0, rawDuration)
        : 0;
      payload.index = Number.isFinite(Number(payload.index))
        ? Math.max(0, Math.floor(Number(payload.index)))
        : 0;
      if (
        payload.playlist_id === "__none__" ||
        payload.group_id === "__none__"
      ) {
        payload.playlist_id = "";
        payload.group_id = "";
      }
      if (payload.playlist_id && !payload.group_id)
        payload.group_id = payload.playlist_id;
      if (payload.group_id && !payload.playlist_id)
        payload.playlist_id = payload.group_id;

      if (String(payload.video_url || "").trim()) {
        const { data: metadata } = await api.post("/me/videos/metadata", {
          url: payload.video_url,
        });
        if (metadata) {
          payload.duration =
            Number.isFinite(Number(metadata.duration)) &&
            Number(metadata.duration) > 0
              ? Math.round(Number(metadata.duration))
              : payload.duration;
          if (!String(payload.title || "").trim() && metadata.title) {
            payload.title = metadata.title;
          }
          if (!String(payload.thumbnail || "").trim() && metadata.thumbnail) {
            payload.thumbnail = metadata.thumbnail;
          }
        }
      }

      const normalizedVideoGpx = normalizeGpxPointsInput(
        payload.gpxdata && normalizeGpxPointsInput(payload.gpxdata).length > 0
          ? payload.gpxdata
          : payload.gpx_points || payload.gpxPoints || [],
      );
      payload.gpxdata = normalizedVideoGpx;
      payload.gpx_points = normalizedVideoGpx;

      payload.markers = ensureMarkers(payload, routes);

      if (editVideo.id) {
        await api.put(`/admin/videos/${editVideo.id}`, payload);
        toast.success("Video updated");
      } else {
        await api.post("/admin/videos", payload);
        toast.success("Video created");
      }
      setEditVideo(null);
      loadAll();
    } catch {
      toast.error("Failed to save video");
    }
  };

  const saveTour = async () => {
    try {
      const payload = {
        ...editTour,
        markers: normalizeTourMarkersForSave(
          editTour?.markers || [],
          getRoutePointsByID(routes, editTour?.route_id),
        ),
      };
      if (editTour.id) {
        await api.put(`/admin/tours/${editTour.id}`, payload);
        toast.success("Tour updated");
      } else {
        await api.post("/admin/tours", payload);
        toast.success("Tour created");
      }
      setEditTour(null);
      loadAll();
    } catch {
      toast.error("Failed to save tour");
    }
  };

  const entitleUserWithTour = async (userID) => {
    const tourID = selectedTourByUser[userID];
    if (!tourID) {
      toast.error("Choose a tour first");
      return;
    }

    setEntitlingUserIDs((prev) => {
      const next = new Set(prev);
      next.add(userID);
      return next;
    });

    try {
      await api.post("/admin/purchases", { user_id: userID, tour_id: tourID });
      toast.success("User entitled to tour");
      loadAll();
    } catch {
      toast.error("Failed to entitle user");
    } finally {
      setEntitlingUserIDs((prev) => {
        const next = new Set(prev);
        next.delete(userID);
        return next;
      });
    }
  };

  const reprocessVideo = async (video) => {
    const status =
      String(video?.status || "")
        .trim()
        .toLowerCase() || "created";
    if (status !== "failed" && status !== "ready") return;

    setReprocessingVideoIDs((prev) => {
      const next = new Set(prev);
      next.add(video.id);
      return next;
    });

    try {
      await api.post(`/admin/videos/${video.id}/reprocess`);
      toast.success("Video reprocessing started");
      loadAll();
    } catch {
      toast.error("Failed to reprocess video");
    } finally {
      setReprocessingVideoIDs((prev) => {
        const next = new Set(prev);
        next.delete(video.id);
        return next;
      });
    }
  };

  const markerRoutePoints = useMemo(() => {
    if (!markerVideo?.route_id) return [];
    return getRoutePointsByID(routes, markerVideo.route_id);
  }, [markerVideo?.route_id, routes]);

  useEffect(() => {
    if (!markerVideo) {
      setMarkerDraft([]);
      setMarkerCurrentTime(0);
      setMarkerDuration(0);
      setSelectedMarkerIndex(null);
      setIsMarkerSelectionLocked(true);
      setStaticMarkerLengthSec(10);
      setStaticMarkerRadiusMeters(100);
      setMapPickMode(null);
      return;
    }
    setMarkerCurrentTime(0);
    setMarkerDuration(Number(markerVideo.duration) || 0);
    setSelectedMarkerIndex(0);
    setIsMarkerSelectionLocked(true);
    setStaticMarkerLengthSec(10);
    setStaticMarkerRadiusMeters(100);
    setMapPickMode(null);
    setMarkerDraft(ensureMarkers(markerVideo, routes));
  }, [markerVideo, routes]);

  const [subsetStartIndex, setSubsetStartIndex] = useState(null);
  const [subsetEndIndex, setSubsetEndIndex] = useState(null);

  useEffect(() => {
    setSubsetStartIndex(null);
    setSubsetEndIndex(null);
  }, [editPlaylist, editRoute]);

  useEffect(() => {
    if (!isMarkerSelectionLocked || mapPickMode || markerDraft.length === 0)
      return;

    const now = Number(markerCurrentTime) || 0;
    const activeIdx = markerDraft.findIndex((m) => {
      const from = roundToTenth(Number(m?.from_sec) || 0);
      const to = roundToTenth(Math.max(from, Number(m?.to_sec) || from));
      return now >= from && now <= to;
    });

    if (activeIdx >= 0 && activeIdx !== selectedMarkerIndex) {
      setSelectedMarkerIndex(activeIdx);
    }
  }, [
    isMarkerSelectionLocked,
    mapPickMode,
    markerCurrentTime,
    markerDraft,
    selectedMarkerIndex,
  ]);

  const savePlaylist = async (playlist) => {
    try {
      if (playlist.id) {
        await api.put(`/admin/playlists/${playlist.id}`, playlist);
        toast.success("Playlist updated");
      } else {
        await api.post(`/admin/playlists`, playlist);
        toast.success("Playlist created");
      }
      loadAll();
    } catch {
      toast.error("Failed to save playlist");
    }
  };

  const openPlaylistEditor = async (playlist) => {
    const detailedPlaylist = playlist?.id
      ? (await ensurePlaylistDetails(playlist.id)) || playlist
      : playlist;
    const routeID = playlistRouteContext?.id || detailedPlaylist?.route_id;
    if (routeID) await ensureRouteDetails(routeID);

    setEditPlaylist({
      ...detailedPlaylist,
      status: String(detailedPlaylist?.status || "").trim() || "created",
      route_id: routeID,
      gpx_files: (detailedPlaylist?.gpx_files || [])
        .slice()
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    });
  };

  const delPlaylist = async (id) => {
    if (!globalThis.confirm("Delete this playlist?")) return;
    await api.delete(`/admin/playlists/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const delRoute = async (id) => {
    if (!globalThis.confirm("Delete this route and all its videos?")) return;
    await api.delete(`/admin/routes/${id}`);
    toast.success("Deleted");
    loadAll();
  };
  const delVideo = async (id) => {
    if (!globalThis.confirm("Delete this video?")) return;
    await api.delete(`/admin/videos/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const parseLocalGpxFile = async (file) => parseGpxText(await file.text());

  const updateVideoIndex = async (video, nextIndexRaw) => {
    const nextIndex = Math.max(0, Math.floor(Number(nextIndexRaw) || 0));
    if ((Number(video?.index) || 0) === nextIndex) return;
    try {
      await api.put(`/admin/videos/${video.id}`, {
        ...video,
        index: nextIndex,
      });
      toast.success("Video index updated");
      loadAll();
    } catch {
      toast.error("Failed to update video index");
    }
  };
  const saveCreator = async () => {
    try {
      if (editCreator.id) {
        await api.put(`/admin/creators/${editCreator.id}`, editCreator);
        toast.success("Creator updated");
      } else {
        await api.post(`/admin/creators`, editCreator);
        toast.success("Creator created");
      }
      setEditCreator(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save creator");
    }
  };
  const delCreator = async (id) => {
    if (!globalThis.confirm("Delete this creator?")) return;
    try {
      await api.delete(`/admin/creators/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete creator");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading…
      </div>
    );
  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E]">
          Admin
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-2">
          Admin Panel
        </h1>
        <p className="text-[#5C665F] mt-3">
          Manage routes, films, GPX tracks, POIs, tours, and users.
        </p>

        <Dialog
          open={!!editPlaylist}
          onOpenChange={(o) => !o && setEditPlaylist(null)}
        >
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editPlaylist?.id ? "Edit playlist" : "New playlist"}
              </DialogTitle>
            </DialogHeader>
            <PlaylistForm
              value={editPlaylist}
              onChange={setEditPlaylist}
              routes={routes}
              routeContextPoints={playlistRouteContext?.gpx_points || []}
              routeDisabled={false}
              youtubePlaylistUrlDisabled={!!editPlaylist?.id}
              gpxUploadMode="single"
              parseUploadedGpx={parseLocalGpxFile}
              allowRouteSubsetCopy
              showCreatorFields
              creators={creators}
              showStatusField
              fetchPlaylistMetadata={fetchVideoMetadata}
            />

            <DialogFooter>
              <Button
                onClick={() => {
                  savePlaylist(editPlaylist);
                  setEditPlaylist(null);
                }}
                className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!editVideo}
          onOpenChange={(o) => !o && setEditVideo(null)}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editVideo?.id ? "Edit video" : "New video"}
              </DialogTitle>
            </DialogHeader>
            {editVideo && (
              <StandaloneVideoForm
                value={editVideo}
                onChange={setEditVideo}
                routes={routes}
                playlists={playlists}
                lockedRouteID={videoPlaylistContext?.route_id || ""}
                lockedPlaylistID={videoPlaylistContext?.id || ""}
                parseUploadedGpx={parseAdminGpxFile}
                fetchVideoMetadata={fetchVideoMetadata}
              />
            )}
            <DialogFooter>
              <Button
                onClick={saveVideo}
                className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white"
                data-testid="save-video-button"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <VideoMarkersEditor
          video={markerVideo}
          routes={routes}
          editingRoute={playlistRouteContext}
          editingPlaylist={videoPlaylistContext}
          onClose={() => setMarkerVideo(null)}
          onSaved={() => {
            setMarkerVideo(null);
          }}
          isAdmin
        />

        <Dialog
          open={!!playlistRouteContext}
          onOpenChange={(o) => {
            if (!o) {
              setPlaylistRouteContext(null);
              setVideoPlaylistContext(null);
            }
          }}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Playlists for {playlistRouteContext?.name || "Route"}
              </DialogTitle>
            </DialogHeader>

            <div className="flex items-center justify-between">
              <div className="text-sm text-[#5C665F]">
                Create and manage playlists scoped to this route.
              </div>
              <Button
                onClick={() =>
                  setEditPlaylist({
                    title: "",
                    description: "",
                    youtube_playlist_url: "",
                    status: "created",
                    creator_id: "",
                    creator_name: "",
                    creator_url: "",
                    route_id: playlistRouteContext?.id || "",
                    pois: [],
                    gpx_files: [],
                  })
                }
                className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white rounded-full"
                data-testid="new-playlist-button"
              >
                <PlusIcon size={16} className="mr-1" /> New playlist
              </Button>
            </div>

            <div
              className="mt-4 bg-white border border-[#E5E0D8] rounded-2xl divide-y divide-[#E5E0D8]"
              data-testid="admin-playlists-list"
            >
              {contextPlaylists.map((g) => (
                <div
                  key={g.id}
                  className="p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={playlistRouteContext?.hero_image || ""}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover border border-[#E5E0D8] bg-[#F4F1EA]"
                    />
                    <div>
                      <div className="font-bold">{g.title}</div>
                      <div className="text-xs text-[#5C665F]">
                        {g.creator_name ? `by ${g.creator_name}` : "No creator"}
                        {g.description ? ` · ${g.description}` : ""}
                        {` · ${(g.pois || []).length} POIs`}
                        {" · Status: "}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#EAF4EF] text-[#2C5545] font-semibold capitalize">
                          {String(g.status || "").trim() || "created"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const detailedPlaylist =
                          (await ensurePlaylistDetails(g.id)) || g;
                        if (detailedPlaylist?.route_id) {
                          await ensureRouteDetails(detailedPlaylist.route_id);
                        }
                        setVideoPlaylistContext(detailedPlaylist);
                      }}
                      data-testid={`playlist-videos-${g.id}`}
                    >
                      Videos
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openPlaylistEditor(g)}
                      data-testid={`edit-playlist-${g.id}`}
                    >
                      <PencilIcon size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => delPlaylist(g.id)}
                      data-testid={`delete-playlist-${g.id}`}
                    >
                      <TrashIcon size={16} />
                    </Button>
                  </div>
                </div>
              ))}
              {contextPlaylists.length === 0 && (
                <div className="p-4 text-sm text-[#5C665F]">
                  No playlists for this route yet.
                </div>
              )}
            </div>
            <Pagination
              page={contextPlaylistsPage}
              pageSize={contextPlaylistsPageSize}
              total={contextPlaylistsTotal}
              onPageChange={setContextPlaylistsPage}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!videoPlaylistContext}
          onOpenChange={(o) => {
            if (!o) setVideoPlaylistContext(null);
          }}
        >
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Videos in {videoPlaylistContext?.title || "Playlist"}
              </DialogTitle>
            </DialogHeader>

            <div className="flex items-center justify-between">
              <div className="text-sm text-[#5C665F]">
                Videos are editable only within the selected playlist context.
              </div>
              <Button
                onClick={async () => {
                  if (videoPlaylistContext?.route_id) {
                    await ensureRouteDetails(videoPlaylistContext.route_id);
                  }
                  setEditVideo({
                    ...{},
                    route_id: videoPlaylistContext?.route_id || "",
                    playlist_id: videoPlaylistContext?.id || "",
                    group_id: videoPlaylistContext?.id || "",
                    creator: videoPlaylistContext?.creator_name || "",
                  });
                }}
                className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white rounded-full"
                data-testid="new-video-button"
              >
                <PlusIcon size={16} className="mr-1" /> New video
              </Button>
            </div>

            <div
              className="mt-4 bg-white border border-[#E5E0D8] rounded-2xl divide-y divide-[#E5E0D8]"
              data-testid="admin-videos-list"
            >
              {contextVideos.map((v) => {
                const route = routes.find((r) => r.id === v.route_id);
                const creator = creators.find((c) => c.id === v.creator_id);
                const creatorDisplay = creator?.name || v.creator || "—";
                const status = String(v.status || "").trim() || "created";
                const statusLower = status.toLowerCase();
                const canReprocess =
                  statusLower === "failed" || statusLower === "ready";
                const isReprocessing = reprocessingVideoIDs.has(v.id);
                return (
                  <div
                    key={v.id}
                    className="p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={v.thumbnail}
                        alt=""
                        className="w-20 h-12 object-cover rounded-lg"
                      />
                      <div>
                        <div className="font-bold">{v.title}</div>
                        <div className="text-xs text-[#5C665F]">
                          Index: {Number(v.index) || 0} ·{" "}
                          {route?.name || v.route_id} · {creatorDisplay} ·{" "}
                          {(v.pois || []).length} POIs · Status:{" "}
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#EAF4EF] text-[#2C5545] font-semibold capitalize">
                            {status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min={0}
                        className="w-20"
                        value={v.index ?? 0}
                        onChange={(e) => {
                          const next = Math.max(
                            0,
                            parseInt(e.target.value || "0", 10) || 0,
                          );
                          setVideos((prev) =>
                            prev.map((item) =>
                              item.id === v.id
                                ? { ...item, index: next }
                                : item,
                            ),
                          );
                        }}
                        onBlur={(e) => updateVideoIndex(v, e.target.value)}
                        aria-label={`Video index for ${v.title}`}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/routes/${v.route_id}?video=${encodeURIComponent(v.id)}`,
                          )
                        }
                        data-testid={`view-video-${v.id}`}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          const detailedVideo =
                            (await ensureVideoDetails(v.id)) || v;
                          let detailedRoute = null;
                          if (detailedVideo?.route_id) {
                            detailedRoute = await ensureRouteDetails(
                              detailedVideo.route_id,
                            );
                          }
                          const routesForMarkers = detailedRoute
                            ? routes.some(
                                (route) => route.id === detailedRoute.id,
                              )
                              ? routes.map((route) =>
                                  route.id === detailedRoute.id
                                    ? { ...route, ...detailedRoute }
                                    : route,
                                )
                              : [...routes, detailedRoute]
                            : routes;
                          setMarkerVideo({
                            ...detailedVideo,
                            markers: ensureMarkers(
                              detailedVideo,
                              routesForMarkers,
                            ),
                          });
                        }}
                        data-testid={`edit-markers-${v.id}`}
                      >
                        Markers
                      </Button>
                      {canReprocess && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isReprocessing}
                          onClick={() => reprocessVideo(v)}
                          data-testid={`reprocess-video-${v.id}`}
                        >
                          {isReprocessing ? "Reprocessing..." : "Reprocess"}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          const detailedVideo =
                            (await ensureVideoDetails(v.id)) || v;
                          if (detailedVideo?.route_id) {
                            await ensureRouteDetails(detailedVideo.route_id);
                          }
                          setEditVideo({
                            ...detailedVideo,
                            route_id:
                              videoPlaylistContext?.route_id ||
                              detailedVideo.route_id,
                            playlist_id:
                              videoPlaylistContext?.id ||
                              detailedVideo.playlist_id ||
                              detailedVideo.group_id ||
                              "",
                            group_id:
                              videoPlaylistContext?.id ||
                              detailedVideo.group_id ||
                              detailedVideo.playlist_id ||
                              "",
                          });
                        }}
                        data-testid={`edit-video-${v.id}`}
                      >
                        <PencilIcon size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => delVideo(v.id)}
                        data-testid={`delete-video-${v.id}`}
                      >
                        <TrashIcon size={16} />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {contextVideos.length === 0 && (
                <div className="p-4 text-sm text-[#5C665F]">
                  No videos in this playlist yet.
                </div>
              )}
            </div>

            <Pagination
              page={contextToursPage}
              pageSize={contextToursPageSize}
              total={contextToursTotal}
              onPageChange={setContextToursPage}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!tourRouteContext}
          onOpenChange={(o) => {
            if (!o) {
              setTourRouteContext(null);
              setEditTour(null);
            }
          }}
        >
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Tours for {tourRouteContext?.name || "Route"}
              </DialogTitle>
            </DialogHeader>

            <div className="flex items-center justify-between">
              <div className="text-sm text-[#5C665F]">
                Tours are manageable only within the selected route context.
              </div>
              <Button
                onClick={() =>
                  setEditTour({
                    ...emptyTour,
                    route_id: tourRouteContext?.id || "",
                    creator_id: creators[0]?.id || "",
                    creator: creators[0]?.name || "",
                  })
                }
                className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white rounded-full"
                data-testid="new-tour-button"
              >
                <PlusIcon size={16} className="mr-1" /> New tour
              </Button>
            </div>

            <div
              className="mt-4 bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden"
              data-testid="admin-route-tours-list"
            >
              <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-wide font-semibold text-[#5C665F] bg-[#F4EFE6] border-b border-[#E5E0D8]">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Creator</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Markers</div>
                <div className="col-span-1">Updated</div>
                <div className="col-span-1">Actions</div>
              </div>
              {contextTours.length === 0 ? (
                <div className="px-4 py-6 text-sm text-[#5C665F]">
                  No tours found for this route.
                </div>
              ) : (
                contextTours.map((tour) => (
                  <div
                    key={tour.id}
                    className="grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 border-[#E5E0D8] items-center"
                  >
                    <div className="col-span-3 font-semibold text-[#1A1D1A]">
                      {tour.name}
                    </div>
                    <div className="col-span-3 text-sm text-[#5C665F]">
                      {tour.creator || "-"}
                    </div>
                    <div className="col-span-2 text-sm text-[#5C665F]">
                      {tour.type === "in_person" ? "In person" : "Virtual"}
                    </div>
                    <div className="col-span-2 text-sm text-[#5C665F]">
                      {(tour.markers || []).length}
                    </div>
                    <div className="col-span-1 text-sm text-[#5C665F]">
                      {tour.updated_at
                        ? new Date(tour.updated_at).toLocaleDateString()
                        : "-"}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setEditTour({
                            ...tour,
                            creator_id: tour.creator_id || "",
                            markers: hydrateTourMarkerLinks(tour.markers || []),
                          })
                        }
                        data-testid={`edit-tour-${tour.id}`}
                      >
                        <PencilIcon size={16} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editTour} onOpenChange={(o) => !o && setEditTour(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editTour?.id ? "Edit tour" : "New tour"}
              </DialogTitle>
            </DialogHeader>
            {editTour && (
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    data-testid="tour-name-input"
                    value={editTour.name || ""}
                    onChange={(e) =>
                      setEditTour({ ...editTour, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Creator</Label>
                  <select
                    data-testid="tour-creator-input"
                    className="w-full border border-[#E5E0D8] rounded-md px-3 py-2"
                    value={editTour.creator_id || ""}
                    onChange={(e) => {
                      const nextCreator = creators.find(
                        (creator) => creator.id === e.target.value,
                      );
                      setEditTour({
                        ...editTour,
                        creator_id: e.target.value,
                        creator: nextCreator?.name || "",
                      });
                    }}
                  >
                    <option value="">Select creator</option>
                    {creators.map((creator) => (
                      <option key={creator.id} value={creator.id}>
                        {creator.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Type</Label>
                  <select
                    data-testid="tour-type-input"
                    className="w-full border border-[#E5E0D8] rounded-md px-3 py-2"
                    value={editTour.type || "virtual"}
                    onChange={(e) =>
                      setEditTour({ ...editTour, type: e.target.value })
                    }
                  >
                    <option value="virtual">Virtual</option>
                    <option value="in_person">In person</option>
                  </select>
                </div>
                <div>
                  <Label>Route</Label>
                  <select
                    data-testid="tour-route-input"
                    className="w-full border border-[#E5E0D8] rounded-md px-3 py-2"
                    value={editTour.route_id || ""}
                    disabled={!!tourRouteContext?.id}
                    onChange={(e) =>
                      setEditTour({ ...editTour, route_id: e.target.value })
                    }
                  >
                    <option value="">Select route</option>
                    {routes.map((route) => (
                      <option key={route.id} value={route.id}>
                        {route.name}
                      </option>
                    ))}
                  </select>
                </div>
                <TourMarkerEditor
                  routeID={editTour.route_id}
                  routes={routes}
                  markers={editTour.markers || []}
                  onChange={(markers) => setEditTour({ ...editTour, markers })}
                />
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={saveTour}
                className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white"
                data-testid="save-tour-button"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="routes" className="mt-10">
          <TabsList>
            <TabsTrigger value="routes" data-testid="tab-routes">
              Routes
            </TabsTrigger>
            <TabsTrigger value="creators" data-testid="tab-creators">
              Creators
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="routes" className="mt-6">
            <Dialog
              open={!!editRoute}
              onOpenChange={(o) => !o && setEditRoute(null)}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => setEditRoute({ ...{} })}
                  className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white rounded-full"
                  data-testid="new-route-button"
                >
                  <PlusIcon size={16} className="mr-1" /> New route
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editRoute?.id ? "Edit route" : "New route"}
                  </DialogTitle>
                </DialogHeader>
                {editRoute && (
                  <StandaloneRouteForm
                    value={editRoute}
                    onChange={setEditRoute}
                    statusOptions={CREATED_READY_STATUS_OPTIONS}
                    fetchExternalMetadata={fetchAdminRouteMetadata}
                    parseUploadedGpx={parseAdminGpxFile}
                  />
                )}
                <DialogFooter>
                  <Button
                    onClick={saveRoute}
                    className="bg-[#b4d3ff] hover:bg-[#5fc2f8] rounded-full"
                    data-testid="save-route-button"
                  >
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="mt-4 flex items-center gap-4">
              <Input
                placeholder="Search routes by name, location, or id"
                value={routesSearch}
                onChange={(e) => {
                  const v = e.target.value;
                  setRoutesSearch(v);
                  if (routesSearchDebounceRef.current)
                    clearTimeout(routesSearchDebounceRef.current);
                  routesSearchDebounceRef.current = setTimeout(() => {
                    setRoutesPage(1);
                    loadRoutes(1).catch(() => {});
                  }, 500);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (routesSearchDebounceRef.current)
                      clearTimeout(routesSearchDebounceRef.current);
                    setRoutesPage(1);
                    loadRoutes(1).catch(() => {});
                  }
                }}
                className="w-96"
              />
              <Button
                onClick={() => {
                  if (routesSearchDebounceRef.current)
                    clearTimeout(routesSearchDebounceRef.current);
                  setRoutesPage(1);
                  loadRoutes(1).catch(() => {});
                }}
                size="sm"
              >
                Search
              </Button>
              <div className="ml-4 flex items-center gap-2">
                <div className="text-xs text-[#5C665F] mr-2">Status:</div>
                <Button
                  size="sm"
                  variant={routesStatusFilter === "" ? "filled" : "outline"}
                  onClick={() => {
                    setRoutesStatusFilter("");
                    if (routesSearchDebounceRef.current)
                      clearTimeout(routesSearchDebounceRef.current);
                    setRoutesPage(1);
                    loadRoutes(1).catch(() => {});
                  }}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={
                    routesStatusFilter === "created" ? "filled" : "outline"
                  }
                  onClick={() => {
                    setRoutesStatusFilter("created");
                    if (routesSearchDebounceRef.current)
                      clearTimeout(routesSearchDebounceRef.current);
                    setRoutesPage(1);
                    loadRoutes(1).catch(() => {});
                  }}
                >
                  Created
                </Button>
                <Button
                  size="sm"
                  variant={
                    routesStatusFilter === "ready" ? "filled" : "outline"
                  }
                  onClick={() => {
                    setRoutesStatusFilter("ready");
                    if (routesSearchDebounceRef.current)
                      clearTimeout(routesSearchDebounceRef.current);
                    setRoutesPage(1);
                    loadRoutes(1).catch(() => {});
                  }}
                >
                  Ready
                </Button>
              </div>
            </div>
            <div
              className="mt-6 bg-white border border-[#E5E0D8] rounded-2xl divide-y divide-[#E5E0D8]"
              data-testid="admin-routes-list"
            >
              {routes.map((r) => (
                <div
                  key={r.id}
                  className="p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={r.hero_image}
                      alt=""
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div>
                      <div className="font-bold">{r.name}</div>
                      <div className="text-xs text-[#5C665F]">
                        {r.location}, {r.country} · {r.difficulty} ·{" "}
                        {r.distance_km} km · {(r.pois || []).length} POIs ·
                        Status:{" "}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#EAF4EF] text-[#2C5545] font-semibold capitalize">
                          {String(r.status || "").trim() || "created"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const detailedRoute =
                          (await ensureRouteDetails(r.id)) || r;
                        setPlaylistRouteContext({ ...r, ...detailedRoute });
                      }}
                      data-testid={`route-playlists-${r.id}`}
                    >
                      Playlists
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTourRouteContext(r)}
                      data-testid={`route-tours-${r.id}`}
                    >
                      Tours
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/routes/${r.id}`)}
                      data-testid={`view-route-${r.id}`}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        const detailedRoute =
                          (await ensureRouteDetails(r.id)) || r;
                        setEditRoute({
                          ...detailedRoute,
                          status:
                            String(
                              detailedRoute?.status || r.status || "",
                            ).trim() || "created",
                        });
                      }}
                      data-testid={`edit-route-${r.id}`}
                    >
                      <PencilIcon size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => delRoute(r.id)}
                      data-testid={`delete-route-${r.id}`}
                    >
                      <TrashIcon size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              page={routesPage}
              pageSize={routesPageSize}
              total={routesTotal}
              onPageChange={setRoutesPage}
            />
          </TabsContent>

          <TabsContent value="creators" className="mt-6">
            <Dialog
              open={!!editCreator}
              onOpenChange={(o) => !o && setEditCreator(null)}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() =>
                    setEditCreator({
                      name: "",
                      url: "",
                      owner_email: "",
                      description: "",
                      avatar_image_url: "",
                      gallery: [],
                    })
                  }
                  className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white rounded-full"
                  data-testid="new-creator-button"
                >
                  <PlusIcon size={16} className="mr-1" /> New creator
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editCreator?.id ? "Edit creator" : "New creator"}
                  </DialogTitle>
                </DialogHeader>
                {editCreator && (
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editCreator.name || ""}
                        onChange={(e) =>
                          setEditCreator({
                            ...editCreator,
                            name: e.target.value,
                          })
                        }
                        placeholder="e.g., Nordic Spokes"
                      />
                    </div>
                    <div>
                      <Label>Website URL (optional)</Label>
                      <Input
                        type="url"
                        value={editCreator.url || ""}
                        onChange={(e) =>
                          setEditCreator({
                            ...editCreator,
                            url: e.target.value,
                          })
                        }
                        placeholder="https://example.com"
                      />
                    </div>
                    <div>
                      <Label>Owner Email (optional)</Label>
                      <Input
                        type="email"
                        value={editCreator.owner_email || ""}
                        onChange={(e) =>
                          setEditCreator({
                            ...editCreator,
                            owner_email: e.target.value,
                          })
                        }
                        placeholder="creator-owner@example.com"
                      />
                    </div>
                    <div>
                      <Label>Avatar Image URL (optional)</Label>
                      <Input
                        type="url"
                        value={editCreator.avatar_image_url || ""}
                        onChange={(e) =>
                          setEditCreator({
                            ...editCreator,
                            avatar_image_url: e.target.value,
                          })
                        }
                        placeholder="https://images.example.com/avatar.jpg"
                      />
                      {editCreator.avatar_image_url ? (
                        <img
                          src={editCreator.avatar_image_url}
                          alt="Creator avatar preview"
                          className="mt-2 w-16 h-16 rounded-full object-cover border border-[#E5E0D8]"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                          onLoad={(e) => {
                            e.target.style.display = "";
                          }}
                        />
                      ) : null}
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Textarea
                        rows={3}
                        value={editCreator.description || ""}
                        onChange={(e) =>
                          setEditCreator({
                            ...editCreator,
                            description: e.target.value,
                          })
                        }
                        placeholder="Short creator bio"
                      />
                    </div>
                    <div>
                      <Label>Gallery URLs (comma or newline separated)</Label>
                      <Textarea
                        rows={4}
                        value={
                          Array.isArray(editCreator.gallery)
                            ? editCreator.gallery.join("\n")
                            : ""
                        }
                        onChange={(e) => {
                          const urls = e.target.value
                            .split(/[\n,]/)
                            .map((s) => s.trim())
                            .filter(Boolean);
                          setEditCreator({ ...editCreator, gallery: urls });
                        }}
                        placeholder="https://images.example.com/1.jpg\nhttps://images.example.com/2.jpg"
                      />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button
                    onClick={saveCreator}
                    className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white"
                  >
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div
              className="mt-6 bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden"
              data-testid="admin-creators-list"
            >
              <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-wide font-semibold text-[#5C665F] bg-[#F4EFE6] border-b border-[#E5E0D8]">
                <div className="col-span-2">Avatar</div>
                <div className="col-span-2">Name</div>
                <div className="col-span-2">Owner</div>
                <div className="col-span-3">Description</div>
                <div className="col-span-2">URL</div>
                <div className="col-span-1">Actions</div>
              </div>
              {creators.length === 0 ? (
                <div className="px-4 py-6 text-sm text-[#5C665F]">
                  No creators found.
                </div>
              ) : (
                creators.map((c) => (
                  <div
                    key={c.id}
                    className="grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 border-[#E5E0D8] items-center"
                  >
                    <div className="col-span-2">
                      {c.avatar_image_url ? (
                        <img
                          src={c.avatar_image_url}
                          alt={`${c.name} avatar`}
                          className="w-10 h-10 rounded-full object-cover border border-[#E5E0D8]"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full border border-dashed border-[#E5E0D8] bg-[#F8F4EC]" />
                      )}
                    </div>
                    <div className="col-span-2">
                      <div className="font-semibold text-[#1A1D1A]">
                        {c.name}
                      </div>
                      <div className="text-xs text-[#8A867C]">
                        {(c.gallery || []).length} gallery image(s)
                      </div>
                    </div>
                    <div className="col-span-2 text-sm text-[#5C665F] break-all">
                      {c.owner_email || "—"}
                    </div>
                    <div className="col-span-3 text-sm text-[#5C665F]">
                      {c.description || "—"}
                    </div>
                    <div className="col-span-2 text-sm text-[#5C665F] break-all">
                      {c.url || "—"}
                    </div>
                    <div className="col-span-1 flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditCreator(c)}
                        data-testid={`edit-creator-${c.id}`}
                      >
                        <PencilIcon size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => delCreator(c.id)}
                        data-testid={`delete-creator-${c.id}`}
                      >
                        <TrashIcon size={14} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <Pagination
              page={creatorsPage}
              pageSize={creatorsPageSize}
              total={creatorsTotal}
              onPageChange={setCreatorsPage}
            />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div
              className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden"
              data-testid="admin-users-list"
            >
              <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-wide font-semibold text-[#5C665F] bg-[#F4EFE6] border-b border-[#E5E0D8]">
                <div className="col-span-3">User</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Role</div>
                <div className="col-span-2">Tours</div>
                <div className="col-span-2">Entitle</div>
              </div>
              {users.length === 0 ? (
                <div className="px-4 py-6 text-sm text-[#5C665F]">
                  No users found.
                </div>
              ) : (
                users.map((u) => (
                  <div
                    key={u.user_id}
                    className="grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 border-[#E5E0D8] items-center"
                  >
                    <div className="col-span-3">
                      <div className="font-semibold text-[#1A1D1A]">
                        {u.name || "Unnamed user"}
                      </div>
                      <div className="text-xs text-[#5C665F]">{u.user_id}</div>
                    </div>
                    <div className="col-span-3 text-sm text-[#1A1D1A] break-all">
                      {u.email}
                    </div>
                    <div className="col-span-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${u.is_admin ? "bg-[#E4F4E9] text-[#2C5545]" : "bg-[#EEEFF1] text-[#525A61]"}`}
                      >
                        {u.is_admin ? "Admin" : "User"}
                      </span>
                    </div>
                    <div
                      className="col-span-2 text-xs text-[#5C665F] space-y-1"
                      data-testid={`user-tour-list-${u.user_id}`}
                    >
                      {(purchases || [])
                        .filter((p) => p.user_id === u.user_id)
                        .map((p) => {
                          const tour = (tours || []).find(
                            (t) => t.id === p.tour_id,
                          );
                          return (
                            <div
                              key={`${p.user_id}-${p.tour_id}`}
                              className="truncate"
                            >
                              {tour?.name || p.tour_id}
                            </div>
                          );
                        })}
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <select
                        className="w-full border border-[#E5E0D8] rounded-md px-2 py-1 text-sm"
                        data-testid={`entitle-tour-select-${u.user_id}`}
                        value={selectedTourByUser[u.user_id] || ""}
                        onChange={(e) =>
                          setSelectedTourByUser((prev) => ({
                            ...prev,
                            [u.user_id]: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select tour</option>
                        {(tours || []).map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        disabled={entitlingUserIDs.has(u.user_id)}
                        data-testid={`entitle-user-${u.user_id}`}
                        onClick={() => entitleUserWithTour(u.user_id)}
                      >
                        {entitlingUserIDs.has(u.user_id) ? "..." : "Add"}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <Pagination
              page={usersPage}
              pageSize={usersPageSize}
              total={usersTotal}
              onPageChange={setUsersPage}
            />
            <div className="mt-4">
              <div className="text-xs text-[#5C665F] mb-2">Purchases</div>
              <Pagination
                page={purchasesPage}
                pageSize={purchasesPageSize}
                total={purchasesTotal}
                onPageChange={setPurchasesPage}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
